import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ticketService } from "../services/ticketService";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

// Query keys
const QUERY_KEYS = {
  TICKETS: "tickets",
  MY_TICKETS: "my-tickets", 
  TICKET_BY_ID: "ticket-by-id",
  TICKET_STATISTICS: "ticket-statistics",
};

// ============================================================================
// TICKET QUERIES
// ============================================================================

/**
 * Get all tickets (admin/manager) or filtered tickets
 */
export const useTicketsQuery = (params = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TICKETS, params],
    queryFn: () => ticketService.getAllTickets(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Get my tickets (client/livreur)
 */
export const useMyTicketsQuery = (params = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.MY_TICKETS, params],
    queryFn: () => ticketService.getMyTickets(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Get ticket by ID
 */
export const useTicketByIdQuery = (id, options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TICKET_BY_ID, id],
    queryFn: () => ticketService.getTicketById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchInterval: 1000 * 30, // Refetch every 30 seconds for real-time updates
    ...options,
  });
};

/**
 * Get ticket statistics (admin/manager only)
 */
export const useTicketStatisticsQuery = (params = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TICKET_STATISTICS, params],
    queryFn: () => ticketService.getTicketStatistics(params),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};

// ============================================================================
// TICKET MUTATIONS
// ============================================================================

/**
 * Create ticket mutation
 */
export const useCreateTicketMutation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ticketService.createTicket,
    onSuccess: (data) => {
      toast.success(t("tickets.createSuccess", "Ticket created successfully"));
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MY_TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKET_STATISTICS] });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || 
        t("tickets.createError", "Failed to create ticket")
      );
    },
  });
};

/**
 * Add message to ticket mutation
 */
export const useAddMessageMutation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, messageData }) => 
      ticketService.addMessage(ticketId, messageData),
    onSuccess: (data, variables) => {
      toast.success(t("tickets.messageAddSuccess", "Message sent successfully"));
      
      // Invalidate ticket details query to get updated messages
      queryClient.invalidateQueries({ 
        queryKey: [QUERY_KEYS.TICKET_BY_ID, variables.ticketId] 
      });
      
      // Invalidate tickets list to update message counts
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MY_TICKETS] });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || 
        t("tickets.messageAddError", "Failed to send message")
      );
    },
  });
};

/**
 * Update ticket mutation (admin/manager only)
 */
export const useUpdateTicketMutation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updateData }) => 
      ticketService.updateTicket(id, updateData),
    onSuccess: (data, variables) => {
      toast.success(t("tickets.updateSuccess", "Ticket updated successfully"));
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ 
        queryKey: [QUERY_KEYS.TICKET_BY_ID, variables.id] 
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MY_TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKET_STATISTICS] });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || 
        t("tickets.updateError", "Failed to update ticket")
      );
    },
  });
};

/**
 * Assign ticket mutation (admin/manager only)
 */
export const useAssignTicketMutation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, assigneeId }) => 
      ticketService.assignTicket(id, assigneeId),
    onSuccess: (data, variables) => {
      toast.success(t("tickets.assignSuccess", "Ticket assigned successfully"));
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ 
        queryKey: [QUERY_KEYS.TICKET_BY_ID, variables.id] 
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKET_STATISTICS] });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || 
        t("tickets.assignError", "Failed to assign ticket")
      );
    },
  });
};

/**
 * Resolve ticket mutation (admin/manager only)
 */
export const useResolveTicketMutation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, resolution }) => 
      ticketService.resolveTicket(id, resolution),
    onSuccess: (data, variables) => {
      toast.success(t("tickets.resolveSuccess", "Ticket resolved successfully"));
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ 
        queryKey: [QUERY_KEYS.TICKET_BY_ID, variables.id] 
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MY_TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKET_STATISTICS] });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || 
        t("tickets.resolveError", "Failed to resolve ticket")
      );
    },
  });
};

/**
 * Close ticket mutation (admin/manager only)
 */
export const useCloseTicketMutation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }) => 
      ticketService.closeTicket(id, reason),
    onSuccess: (data, variables) => {
      toast.success(t("tickets.closeSuccess", "Ticket closed successfully"));
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ 
        queryKey: [QUERY_KEYS.TICKET_BY_ID, variables.id] 
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MY_TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKET_STATISTICS] });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || 
        t("tickets.closeError", "Failed to close ticket")
      );
    },
  });
};

/**
 * Reopen ticket mutation (admin/manager only)
 */
export const useReopenTicketMutation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => ticketService.reopenTicket(id),
    onSuccess: (data, variables) => {
      toast.success(t("tickets.reopenSuccess", "Ticket reopened successfully"));
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ 
        queryKey: [QUERY_KEYS.TICKET_BY_ID, variables] 
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MY_TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKET_STATISTICS] });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || 
        t("tickets.reopenError", "Failed to reopen ticket")
      );
    },
  });
};

/**
 * Add customer feedback mutation
 */
export const useAddFeedbackMutation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, rating, feedback }) => 
      ticketService.addCustomerFeedback(id, rating, feedback),
    onSuccess: (data, variables) => {
      toast.success(t("tickets.feedbackSuccess", "Feedback submitted successfully"));
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ 
        queryKey: [QUERY_KEYS.TICKET_BY_ID, variables.id] 
      });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || 
        t("tickets.feedbackError", "Failed to submit feedback")
      );
    },
  });
};